// src/components/ARInvoiceLines.tsx
//
// Invoice-level drilldown for the AR Detail view: expands under an invoice row
// in the customer drilldown and shows the invoice's line items (customer-invoice
// recipe) with View / Print of the invoice PDF.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Printer, Loader2 } from 'lucide-react';
import { apiV2 as api } from '../services/apiV2';
import { useAuthContext } from '../hooks/useAuth';
import { buildInvoicePdf, openPdf, printPdf, type InvoiceData } from '../lib/customerDocs';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtQty = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v);

const invoiceQuery = (invoice: string) => ({
  queryKey: ['custdoc-inv', invoice],
  queryFn: () => api.getCustomerInvoice(invoice),
  staleTime: 5 * 60_000,
});

/** View / Print icons for one invoice. Shares the line-items query cache, so a
 *  row that's already expanded opens instantly. */
export function InvoicePdfButtons({ invoice, size = 13, onError }: { invoice: string; size?: number; onError?: (msg: string) => void }) {
  const qc = useQueryClient();
  const { branding } = useAuthContext();
  const company = branding?.company_name || 'Invoice';
  const [busy, setBusy] = useState<'view' | 'print' | null>(null);
  const run = (print: boolean) => async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(print ? 'print' : 'view');
    try {
      const inv = await qc.fetchQuery(invoiceQuery(invoice));
      const doc = buildInvoicePdf(inv as InvoiceData, company);
      if (print) printPdf(doc); else openPdf(doc);
    } catch (err) {
      console.error('[invoice-pdf]', err);
      onError?.(`Invoice ${invoice}: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setBusy(null); }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <button onClick={run(false)} disabled={!!busy} className="text-slate-400 hover:text-white disabled:opacity-50" title="View invoice">
        {busy === 'view' ? <Loader2 size={size} className="animate-spin" /> : <Eye size={size} />}
      </button>
      <button onClick={run(true)} disabled={!!busy} className="text-slate-400 hover:text-white disabled:opacity-50" title="Print invoice">
        {busy === 'print' ? <Loader2 size={size} className="animate-spin" /> : <Printer size={size} />}
      </button>
    </span>
  );
}

export default function ARInvoiceLines({ invoice, colSpan }: { invoice: string; colSpan: number }) {
  const { branding } = useAuthContext();
  const company = branding?.company_name || 'Invoice';
  const { data, isLoading, error } = useQuery({ ...invoiceQuery(invoice), retry: 1 });

  return (
    <tr className="bg-slate-900/40">
      <td colSpan={colSpan} className="px-3 py-2">
        {isLoading ? (
          <div className="text-slate-400 py-2"><Loader2 size={13} className="animate-spin inline mr-1" /> Loading invoice…</div>
        ) : error || !data ? (
          <div className="text-red-300 py-2">Could not load invoice {invoice}{error instanceof Error ? `: ${error.message}` : ''}</div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-slate-400">
                Invoice <span className="font-mono text-white">{data.invoice.invoice}</span>
                {data.invoice.terms && <> · Terms {data.invoice.terms}</>}
                {' '}· {data.lines.length} line{data.lines.length === 1 ? '' : 's'}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { const d = buildInvoicePdf(data as InvoiceData, company); openPdf(d); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-700/60 border border-slate-600/50 text-slate-200 hover:bg-slate-700">
                  <Eye size={12} /> View
                </button>
                <button onClick={() => { const d = buildInvoicePdf(data as InvoiceData, company); printPdf(d); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-700/60 border border-slate-600/50 text-slate-200 hover:bg-slate-700">
                  <Printer size={12} /> Print
                </button>
              </div>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-700/30 text-slate-500">
                <th className="px-2 py-1 text-left">Item</th>
                <th className="px-2 py-1 text-left">Description</th>
                <th className="px-2 py-1 text-right">Qty</th>
                <th className="px-2 py-1 text-right">Cases</th>
                <th className="px-2 py-1 text-right">Price</th>
                <th className="px-2 py-1 text-right">Amount</th>
              </tr></thead>
              <tbody>
                {data.lines.map((l, idx) => (
                  <tr key={`${l.item}-${idx}`} className="border-b border-slate-800/30">
                    <td className="px-2 py-1 font-mono text-slate-300">{l.itemNo}</td>
                    <td className="px-2 py-1 text-slate-300">{l.desc}</td>
                    <td className="px-2 py-1 text-right text-slate-300">{fmtQty(l.quantity)}</td>
                    <td className="px-2 py-1 text-right text-slate-400">{l.cases ? fmtQty(l.cases) : ''}</td>
                    <td className="px-2 py-1 text-right text-slate-300">{fmtCur(l.price)}</td>
                    <td className="px-2 py-1 text-right text-white">{fmtCur(l.ext)}</td>
                  </tr>
                ))}
                {data.lines.length === 0 && (
                  <tr><td colSpan={6} className="px-2 py-2 text-slate-500">No line items found for this invoice.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
}
